// Globals
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';

// Models
import { IAppraisal } from '@/models/order.model';

// Components
import { IFormInstance } from '@/components';
import { DatePicker } from '@/ui';
import {
  StyledDateBlock,
  StyledDateBlockTitle,
  StyledDatesWrap,
  StyledDivider,
  StyledForm,
  StyledFormBlockWrap,
  StyledFormItem,
  StyledInput,
  StyledRow,
} from './RateAndConditionReportFormStyles';

// Helpers
import {
  MAX_CONDITION_RESTRICTION_PERIOD,
  MAX_RATE_RESTRICTION_PERIOD,
  TUploadConditionReportFields,
  UploadConditionReportFields,
} from '../../helpers';
import { useGetUploadReportRules } from '../../hooks/useGetFormValidationRules';

type TRateAndConditionReportUpload = {
  onSubmit?: (values: TUploadConditionReportFields) => void;
  onValuesChange?: (values: TUploadConditionReportFields) => void;
  formInstance?: IFormInstance;
  initialValues?: IAppraisal;
};

const RateAndConditionReportForm = ({
  formInstance,
  onValuesChange,
  onSubmit,
  initialValues,
}: TRateAndConditionReportUpload) => {
  const { t } = useTranslation('order');
  const rules = useGetUploadReportRules();

  return (
    <StyledForm
      form={formInstance}
      onFinish={onSubmit}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
      requiredMark={false}
      name={'report'}
    >
      <StyledFormBlockWrap>
        <StyledDatesWrap>
          <StyledDateBlock>
            <StyledRow>
              <StyledDateBlockTitle>{t('rate_and_condition.condition_label')}</StyledDateBlockTitle>
              <span>
                <Trans
                  i18nKey="rate_and_condition.restriction_period"
                  ns="order"
                  components={{
                    strong: <strong />,
                  }}
                  values={{ months: MAX_CONDITION_RESTRICTION_PERIOD }}
                />
              </span>
            </StyledRow>
            <StyledFormItem
              label={t('rate_and_condition.condition_date_label')}
              rules={rules[UploadConditionReportFields.CONDITION_REPORT_CREATED_AT]}
              name={UploadConditionReportFields.CONDITION_REPORT_CREATED_AT}
            >
              <DatePicker
                disabledDate={(current) => {
                  return current.isBefore(dayjs().subtract(MAX_CONDITION_RESTRICTION_PERIOD, 'month'), 'day');
                }}
              />
            </StyledFormItem>
          </StyledDateBlock>
          <StyledDateBlock>
            <StyledRow>
              <StyledDateBlockTitle>{t('rate_and_condition.rate_label')}</StyledDateBlockTitle>
              <span>
                <Trans
                  i18nKey="rate_and_condition.restriction_period"
                  ns="order"
                  components={{
                    strong: <strong />,
                  }}
                  values={{ months: MAX_RATE_RESTRICTION_PERIOD }}
                />
              </span>
            </StyledRow>
            <StyledFormItem
              label={t('rate_and_condition.rate_date_label')}
              rules={rules[UploadConditionReportFields.RATE_REPORT_CREATED_AT]}
              name={UploadConditionReportFields.RATE_REPORT_CREATED_AT}
            >
              <DatePicker
                disabledDate={(current) => {
                  return current.isBefore(dayjs().subtract(MAX_RATE_RESTRICTION_PERIOD, 'month'), 'day');
                }}
              />
            </StyledFormItem>
          </StyledDateBlock>
        </StyledDatesWrap>
      </StyledFormBlockWrap>
      <StyledDivider />
      <StyledFormBlockWrap>
        <StyledFormItem
          label={t('rate_and_condition.price_label')}
          name={UploadConditionReportFields.PRICE}
          rules={rules[UploadConditionReportFields.PRICE]}
        >
          <StyledInput />
        </StyledFormItem>
      </StyledFormBlockWrap>
    </StyledForm>
  );
};

export default RateAndConditionReportForm;
